import { memo, useCallback, useMemo, useState } from 'react'
import { z } from 'zod'
import { type Node, type NodeProps, Position } from '@xyflow/react'
import { useShallow } from 'zustand/shallow'
import { useFlowStore } from '@/stores/flow-store'
import { useDeleteNode } from '@/hooks/use-delete-node'
import CustomHandle from '@/features/flow/_builder/components/handles/custom-handler'
import {
  NodeCard,
  NodeCardContent,
  NodeCardDescription,
  NodeCardFooter,
  NodeCardHeader,
} from '@/features/flow/_builder/components/ui/node-card'
import ConditionNodePropertyPanel from '../../sidebar/panels/node-properties/property-panels/condition-property-panel'
import { BuilderNode, RegisterNodeMetadata } from '../../types'
import { getNodeDetail } from '../../utils'

const NODE_TYPE = BuilderNode.CONDITIONAL_PATH

const ConditionNodeDataSchema = z.object({
  conditionType: z.enum(['tags', 'fields', 'values']),
  left: z.string().min(1, 'Valor da esquerda é obrigatório'),
  operator: z.enum(['eq', 'gt', 'lt', 'contains']),
  right: z.string().min(1, 'Valor da direita é obrigatório'),
  deletable: z.boolean().optional(),
})

export type ComparisonOperator = 'eq' | 'gt' | 'lt' | 'contains'
export type ConditionType = 'tags' | 'fields' | 'values'
export type ConditionNodeData = z.infer<typeof ConditionNodeDataSchema>

type ConditionNodeProps = NodeProps<Node<ConditionNodeData, typeof NODE_TYPE>>

export function ConditionNode({
  id,
  isConnectable,
  selected,
  data,
}: ConditionNodeProps) {
  const meta = useMemo(() => getNodeDetail(NODE_TYPE), [])

  const [showNodePropertiesOf] = useFlowStore(
    useShallow((s) => [s.actions.sidebar.showNodePropertiesOf])
  )

  const [trueHandleId] = useState<string>(`${id}-true`)
  const [falseHandleId] = useState<string>(`${id}-false`)
  const [targetHandleId] = useState<string>(`${id}-target`)

  const deleteNode = useDeleteNode()

  const handleDeleteNode = () => {
    deleteNode(id)
  }

  const handleShowNodeProperties = useCallback(() => {
    showNodePropertiesOf({ id, type: NODE_TYPE })
  }, [id, showNodePropertiesOf])

  return (
    <NodeCard
      data-selected={selected}
      onDoubleClick={handleShowNodeProperties}
      nodeId={id}
    >
      <NodeCardHeader
        icon={meta.icon}
        title={meta.title}
        handleDeleteNode={handleDeleteNode}
        handleShowNodeProperties={handleShowNodeProperties}
        gradientColor={meta.gradientColor}
      />

      <NodeCardContent>
        <div className='flex flex-col p-4'>
          <div className='text-xs font-medium text-card-foreground'>
            Condição
          </div>
          <div className='mt-2 text-sm leading-snug'>
            <span className='rounded bg-card-foreground/10 px-1 py-0.5'>
              {data.left || 'valor'}
            </span>
            <span className='px-1'>
              {data.operator === 'eq' && 'igual a'}
              {data.operator === 'gt' && 'maior que'}
              {data.operator === 'lt' && 'menor que'}
              {data.operator === 'contains' && 'contém'}
            </span>
            <span className='rounded bg-card-foreground/10 px-1 py-0.5'>
              {data.right || 'valor'}
            </span>
          </div>
          <div className='mt-2 text-xs text-card-foreground/70'>
            Tipo: {data.conditionType}
          </div>
        </div>

        <NodeCardDescription description='Avalia a condição e segue pelo caminho apropriado' />

        <NodeCardFooter nodeId={id} />
      </NodeCardContent>

      <CustomHandle
        type='target'
        id={targetHandleId}
        position={Position.Left}
        isConnectable={isConnectable}
      />

      <CustomHandle
        type='source'
        id={trueHandleId}
        position={Position.Right}
        className='-mt-8 !border-green-500'
        isConnectable={isConnectable}
      />

      <CustomHandle
        type='source'
        id={falseHandleId}
        position={Position.Right}
        className='!border-red-500'
        isConnectable={isConnectable}
      />
    </NodeCard>
  )
}

export const metadata: RegisterNodeMetadata<ConditionNodeData> = {
  type: NODE_TYPE,
  node: memo(ConditionNode),
  detail: {
    icon: 'git-branch',
    title: 'Condição',
    description: 'Cria lógica condicional no fluxo (verdadeiro/falso).',
    gradientColor: 'teal',
  },
  connection: {
    inputs: 1,
    outputs: 2,
  },
  defaultData: {
    conditionType: 'values',
    left: '',
    operator: 'eq',
    right: '',
  },
  propertyPanel: ConditionNodePropertyPanel,
  validationSchema: ConditionNodeDataSchema,
}
